/**
 * Ported from the lekterion codebase (`src/auth/LoginButton.tsx`) @ `d942f2f`.
 * Bindery is canonical for this module from 2026-09-06; reconcile source-side
 * drift toward this copy.
 *
 * One local modification dropped in the port: the source's icon carried
 * `data-icon="inline-start"`, which is a hook in the `radix-sera` shadcn style
 * it vendored (`has-data-[icon=inline-start]:pl-4` on the button's own
 * variants). `@bindery/ui`'s button is the `new-york` style and reads no such
 * attribute, so it is removed rather than left as a no-op that looks load-
 * bearing. The icon's spacing comes from the button's own `gap-2`.
 */
import { LogIn } from "lucide-react";
import { type JSX } from "react";
import { Link } from "react-router";

import { useLightAuth } from "@/auth/context";
import { Button } from "@bindery/ui/components/ui/button";

/**
 * Header session control (Phase 8). Anonymous readers get a "Sign in" button
 * (public Google lane); signed-in readers see who they are and a sign-out, plus
 * an Admin link when the session is privileged.
 */
export function SignInButton(): JSX.Element {
  const { isAuthenticated, isAdmin, nickname, signIn, signOut } =
    useLightAuth();

  if (!isAuthenticated) {
    return (
      <Button variant="outline" size="sm" onClick={signIn}>
        <LogIn aria-hidden="true" />
        Sign in
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {isAdmin && (
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin">Admin</Link>
        </Button>
      )}
      <span className="text-muted-foreground text-xs font-semibold tracking-widest uppercase">
        {nickname ?? (isAdmin ? "Admin" : "Reader")}
      </span>
      <Button variant="ghost" size="sm" onClick={signOut}>
        Sign out
      </Button>
    </div>
  );
}
